import axios from "axios";

const ACK_OK = "$OK#";
const ACK_ERR = "$ERR#";
const ACK_TIMEOUT_MS = 60000;
const INTER_MESSAGE_DELAY_MS = 500;
const MAX_RETRIES_PER_MESSAGE = 3;
const CERT_CHUNK_SIZE = 64;
const CERT_CHUNK_DELAY_MS = 10;

type AckValue = typeof ACK_OK | typeof ACK_ERR;

type AckWaiter = {
  label: string;
  resolve: (ack: AckValue) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

export type SerialResult = {
  success: true;
  serialCompleted: true;
  ack_config: typeof ACK_OK;
  ack_info: typeof ACK_OK;
  ack_cert: typeof ACK_OK;
  attempts_config: number;
  attempts_info: number;
  attempts_cert: number;
};

export async function getCertificateBase64(tenant: string): Promise<string> {
  const res = await axios.get("/api/serial/cert", {
    headers: { "X-Tenant-Key": tenant },
    withCredentials: true,
  });
  return typeof res.data?.cert_b64 === "string" ? res.data.cert_b64 : "";
}

function maskPassword(configMsg: string): string {
  const parts = configMsg.split("#");
  if (parts.length >= 3 && parts[0] === "$CONFIG") {
    const pw = parts[2];
    parts[2] = pw.length <= 2 ? "**" : pw.slice(0, 2) + "***";
  }
  return parts.join("#");
}

function previewMessage(label: string, message: string): string {
  if (label === "CONFIG") return maskPassword(message);
  if (label === "CERT") return `[CERT: ${new TextEncoder().encode(message).length} byte]`;
  return message;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function sendToSelectedSerialPort(
  port: any,
  configMsg: string,
  infoMsg: string,
  certMsg: string
): Promise<SerialResult> {
  if (!port) throw new Error("Porta seriale non selezionata");

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  let writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  let readTask: Promise<void> | null = null;
  let opened = false;
  let closing = false;
  let rxFailure: Error | null = null;
  let pendingAck: AckWaiter | null = null;
  let streamBuffer = "";
  let lineBuffer = "";
  const decoder = new TextDecoder();
  const encoder = new TextEncoder();

  function settleAck(ack: AckValue): void {
    if (!pendingAck) return;
    const waiter = pendingAck;
    pendingAck = null;
    clearTimeout(waiter.timer);
    console.log(`${waiter.label}: ACK rilevato -> ${ack}`);
    waiter.resolve(ack);
  }

  function failAck(error: Error): void {
    if (!pendingAck) return;
    const waiter = pendingAck;
    pendingAck = null;
    clearTimeout(waiter.timer);
    waiter.reject(error);
  }

  function processReceivedText(chunk: string): void {
    console.log("RX SERIAL raw chunk=", JSON.stringify(chunk));
    streamBuffer += chunk;
    // Il firmware invia ACK brevi: manteniamo abbastanza caratteri per
    // riconoscere anche un ACK diviso tra pacchetti USB.
    if (streamBuffer.length > 512) streamBuffer = streamBuffer.slice(-256);

    // Evita che un ACK residuo venga attribuito al messaggio successivo.
    if (pendingAck) {
      const okIndex = streamBuffer.indexOf(ACK_OK);
      const errIndex = streamBuffer.indexOf(ACK_ERR);
      if (okIndex !== -1 || errIndex !== -1) {
        const isOk = okIndex !== -1 && (errIndex === -1 || okIndex < errIndex);
        streamBuffer = "";
        settleAck(isOk ? ACK_OK : ACK_ERR);
      }
    }

    lineBuffer += chunk;
    const lines = lineBuffer.replace(/\r/g, "").split("\n");
    lineBuffer = lines.pop() ?? "";
    for (const line of lines) {
      if (line.trim()) console.log("RX SERIAL raw line=", line.trim());
    }
    if (lineBuffer.length > 8192) {
      console.warn("RX SERIAL: riga troppo lunga, buffer diagnostico azzerato");
      lineBuffer = "";
    }
  }

  async function receiveLoop(): Promise<void> {
    if (!reader) return;
    try {
      while (!closing) {
        const { value, done } = await reader.read();
        if (done) {
          if (!closing) throw new Error("Porta seriale chiusa durante la lettura");
          break;
        }
        if (value?.length) {
          const text = decoder.decode(value, { stream: true });
          if (text) processReceivedText(text);
        }
      }
    } catch (error) {
      if (!closing) {
        rxFailure = error instanceof Error ? error : new Error(String(error));
        console.error("RX SERIAL: errore di lettura", rxFailure);
        failAck(rxFailure);
      }
    }
  }

  function waitForAck(label: string, timeoutMs: number): Promise<AckValue> {
    if (rxFailure) return Promise.reject(rxFailure);
    if (pendingAck) return Promise.reject(new Error("Un'altra attesa ACK è già attiva"));
    // Scarta risposte pregresse prima di iniziare un nuovo tentativo.
    streamBuffer = "";
    return new Promise<AckValue>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (pendingAck) {
          pendingAck = null;
          console.warn(`${label}: timeout ACK dopo ${timeoutMs} ms`);
          reject(new Error(`${label}: timeout in attesa di ACK dopo ${timeoutMs} ms`));
        }
      }, timeoutMs);
      pendingAck = { label, resolve, reject, timer };
    });
  }

  async function writeMessage(message: string, label: string): Promise<void> {
    if (!writer) throw new Error("Porta seriale non disponibile per la scrittura");
    console.log(`==== SERIAL SEND ${label} START ====`);
    console.log(previewMessage(label, message));
    console.log(`==== SERIAL SEND ${label} END ====`);

    if (label !== "CERT") {
      await writer.write(encoder.encode(message));
      return;
    }
    // Manteniamo la frammentazione usata in precedenza per il certificato.
    for (let i = 0; i < message.length; i += CERT_CHUNK_SIZE) {
      await writer.write(encoder.encode(message.slice(i, i + CERT_CHUNK_SIZE)));
      await sleep(CERT_CHUNK_DELAY_MS);
    }
  }

  async function sendWithAckRetry(
    label: string,
    payload: string
  ): Promise<{ ack: typeof ACK_OK; attempts: number }> {
    for (let attempt = 1; attempt <= MAX_RETRIES_PER_MESSAGE; attempt++) {
      if (rxFailure) throw rxFailure;
      console.log(`Invio ${label}, tentativo ${attempt}/${MAX_RETRIES_PER_MESSAGE}`);

      // L'attesa viene attivata PRIMA dell'invio, così un ACK molto veloce
      // non può arrivare prima che React inizi ad ascoltarlo.
      const ackPromise = waitForAck(label, ACK_TIMEOUT_MS);
      // Installa subito un handler anche se la scrittura fallisce prima dell'await.
      void ackPromise.catch(() => {});
      let ack: AckValue;
      try {
        await writeMessage(payload, label);
        console.log(`Attendo ACK ${label} dalla STM32 (max ${ACK_TIMEOUT_MS / 1000}s)...`);
        ack = await ackPromise;
      } catch (error) {
        // In caso di errore di scrittura, chiudiamo l'attesa senza lasciare
        // promesse pendenti; il retry è riservato ai timeout/ERR.
        if (pendingAck) {
          failAck(error instanceof Error ? error : new Error(String(error)));
          void ackPromise.catch(() => {});
        }
        if (rxFailure) throw rxFailure;
        const message = error instanceof Error ? error.message : String(error);
        if (!message.includes("timeout in attesa di ACK")) throw error;
        console.warn(`${label}: timeout al tentativo ${attempt}`);
        if (attempt === MAX_RETRIES_PER_MESSAGE) throw error;
        await sleep(INTER_MESSAGE_DELAY_MS);
        continue;
      }

      if (ack === ACK_OK) return { ack, attempts: attempt };
      console.warn(`${label}: ricevuto ${ACK_ERR} al tentativo ${attempt}`);
      if (attempt === MAX_RETRIES_PER_MESSAGE) {
        throw new Error(`${label}: ricevuto ${ACK_ERR} per ${MAX_RETRIES_PER_MESSAGE} tentativi`);
      }
      await sleep(INTER_MESSAGE_DELAY_MS);
    }
    throw new Error(`${label}: tentativi esauriti`);
  }

  try {
    await port.open({
      baudRate: 115200,
      dataBits: 8,
      stopBits: 1,
      parity: "none",
      flowControl: "none",
    });
    opened = true;
    if (!port.readable || !port.writable) {
      throw new Error("La porta seriale non espone lettura e scrittura");
    }
    reader = port.readable.getReader();
    writer = port.writable.getWriter();
    readTask = receiveLoop();

    const config = await sendWithAckRetry("CONFIG", configMsg);
    await sleep(INTER_MESSAGE_DELAY_MS);
    const info = await sendWithAckRetry("INFO", infoMsg);
    await sleep(INTER_MESSAGE_DELAY_MS);
    const cert = await sendWithAckRetry("CERT", certMsg);

    console.log("Tutti gli ACK sono stati ricevuti");
    return {
      success: true,
      serialCompleted: true,
      ack_config: config.ack,
      ack_info: info.ack,
      ack_cert: cert.ack,
      attempts_config: config.attempts,
      attempts_info: info.attempts,
      attempts_cert: cert.attempts,
    };
  } finally {
    closing = true;
    failAck(new Error("Comunicazione seriale terminata"));
    if (reader) {
      try { await reader.cancel(); } catch (error) {
        console.warn("Impossibile annullare la lettura seriale:", error);
      }
    }
    if (readTask) {
      try { await readTask; } catch { /* receiveLoop gestisce i propri errori */ }
    }
    if (reader) {
      try { reader.releaseLock(); } catch {}
    }
    if (writer) {
      try { writer.releaseLock(); } catch {}
    }
    if (opened) {
      try { await port.close(); } catch (error) {
        console.warn("Impossibile chiudere la porta seriale:", error);
      }
    }
  }
}
